<template>
  <section class="wam-content builder-segment" v-if="hasUserAccessToBuilderSegment">
    <CriteriaList :formula="formulaInstance" />
    <div style="margin-bottom: 40px">
      <VolumeContainer :global-volume="globalVolume" @show-before-edit-modal="triggerEditionModal" @trigger-breakdown="triggerBreakdownMetrics" />

      <FormulaSection :formula="formulaInstance" />
    </div>

    <div class="overlay-close" v-if="displayAudienceForm" @click="closeForm()" />
    <div class="modal" v-if="displayAudienceForm && !displayExportForm" :class="[{ 'modal-h-full modal-form-audience': displayAudienceForm }]">
      <FormAudience
        v-if="displayAudienceForm"
        :mode="'segment'"
        :formula="formulaInstance"
      />
    </div>
    <div class="overlay-close" v-if="displayWarningModal" @click="setDisplayModal({ item: 'warning', display: false })" />
    <div class="modal modal__small modal-warning-emptyboxes" v-if="displayWarningModal">
      <ConfirmationModal modal-class-name="modal-wrapper">
        <template slot="content">
          <div>One or more operator is empty and will be removed.</div>
          <div>Do you want to proceed?</div>
        </template>
        <template slot="footer">
          <div class="footer-content l-flex-row--between">
            <div class="btn btn--medium" @click="setDisplayModal({ item: 'warning', display: false })">
              No
            </div>
            <div class="btn btn--medium btn-rev btn-delete" @click="beforeEditModalAction(audienceInEdition ? 'edit' : 'create')">
              Yes
            </div>
          </div>
        </template>
      </ConfirmationModal>
    </div>

    <div class="overlay-close" v-if="showEditionModal" @click="$emit('closeBeforeEditModal')" />
    <div class="modal modal__large" v-if="showEditionModal">
      <ConfirmationModal modal-class-name="modal-wrapper">
        <template slot="header">
          <div class="title subcategory">
            Do you wish to edit the audience or create a new audience ?
          </div>
        </template>
        <template slot="content" v-if="editedAudienceIsInFormula">
          <el-alert
            v-if="editedAudienceIsInFormula"
            type="warning"
            :closable="false"
            show-icon
          >
            Edit audience feature is unavailable because the audience is a criteria in the current formula. It is however possible to create a new audience.
          </el-alert>
        </template>
        <template slot="footer">
          <div class="footer-content l-flex-row--between">
            <div class="btn btn--medium" @click="closeEditionModal">
              Cancel
            </div>
            <div class="l-flex-row">
              <div class="btn btn--medium btn-rev" :disabled="editedAudienceIsInFormula" @click="beforeEditModalAction('edit')">
                Edit Audience
              </div>
              <div class="btn btn--medium btn-rev" @click="beforeEditModalAction('create')">
                Create Audience
              </div>
            </div>
          </div>
        </template>
      </ConfirmationModal>
    </div>
  </section>
</template>

<script>
import { Formula } from '@node/wam-audience-builder'
import { THROTTLING, VOLUMES } from '@/utils/constants'
import { cloneDeep, debounce, filter } from 'lodash'

//avoid repeating namespace for each store action/mutation/getter/state
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'

//components
import CriteriaList from '@/components/builder/criteriaList/List.vue'
import FormulaSection from '@/components/builder/formula/Formula.vue'
// import BoxFormula from '@/components/builder/BoxFormula'
// import Breakdown from '@/components/common/Breakdown'
import ConfirmationModal from '@/components/common/confirmationModal'
import FormAudience from '@/components/builder/FormAudience'
// import FormulaSettings from '@/components/builder/FormulaSettings.vue'
// import Panel from '@/components/common/Panel'
// import SectionHeader from '@/components/common/SectionHeader'
// import Setting from '@/components/builder/Setting'
// import Tabs from '@/components/common/Tabs'
// import Volumes from '@/components/common/Volumes'

import VolumeContainer from './VolumeContainer.vue'

export default {
    name: 'BuilderAudience',

    components: {
        // BoxFormula,
        // Breakdown,
        ConfirmationModal,
        CriteriaList,
        FormAudience,
        // FormulaSettings,
        // Panel,
        // SectionHeader,
        // Setting,
        // Tabs,
        // Volumes,
        FormulaSection,
        VolumeContainer
    },

    props: {
        showBeforeEditModal: {
            type: Boolean,
            required: true
        }
    },

    data() {
        return {
            activePanel: '1',
            formulaInstance: new Formula({
                onChange: debounce(this.onFormulaChange, 1500, { leading: true }),
                onMessage: this.onFormulaMessage
            }),
            showEditionModal: false,
            latestFormula: null
        }
    },

    watch: {
        criteriaList: {
            handler(value) {
                this.formulaInstance.setDataSet(value, this.criteriaListIsReady)
            },
            immediate: true
        },

        demographicBreakdown(active) {
            if (this.activeTab === 'breakdown' && !active) {
                this.setActiveTabBuilder({ tab: 'volumes' })
            }
        },

        activeTab(tab) {
            if (tab === 'summary') {
                this.getFormulaMetrics({ type: 'Summary', formula: this.formulaInstance })
            }
            if (tab === 'breakdown') {
                this.getFormulaMetrics({ type: 'Breakdown', formula: this.formulaInstance })
            }
        },

        surfIntensity: {
            handler(value) {
                this.manageSurfIntensity(value)
            },
            immediate: true
        },

        quantile: {
            handler(value) {
                this.manageQuantile(value)
            },
            immediate: true
        },

        frequency: {
            handler(value) {
                this.manageFrequency(value)
            },
            immediate: true
        },

        resetFormulaStore: {
            handler(value) {
                if (value) {
                    this.resetFormula(false)
                    this.formulaInstance.refreshFormula()
                    this.formulaInstance.setRecency(30)
                    this.formulaInstance.setQuantile(10)
                    this.formulaInstance.setSurfIntensity(0)
                    for (const index in this.formulaInstance._boxes) {
                        this.formulaInstance._boxes[index].removeAllCriterion()
                    }
                    this.formulaInstance.initializeFormula()
                }
            },
            immediate: true
        }
    },

    computed: {
        ...mapState('builder', {
            breakdown: state => state.formulaBreakdown,
            breakdownLoading: state => state.formulaBreakdownLoading,
            summaryLoading: state => state.formulaSummaryLoading,
            summary: state => state.formulaSummary,
            globalVolume: state => state.formulaGlobalVolume,
            resetFormulaStore: state => state.resetFormula
        }),

        totalVolumeValue() {
            let total = 0
            Object.keys(this.globalVolume).forEach((key) => {

                if (typeof this.globalVolume[key] === 'number') {
                    total += this.globalVolume[key]
                }
            })

            return total
        },

        ...mapState('ui/builder', {
            displayForm: state => state.formAudience.display,
            displayWarningModal: state => state.displayModal.warning,
            audienceFormMode: state => state.formAudience.mode,
            activeTab: state => state.metrics.activeTabBuilder
        }),

        ...mapState('data/settings', {
            showXDevice: state => state.general.xdevice,
            demographicBreakdown: state => state.general.demographicBreakdown,
            quantile: state => state.general.quantile,
            surfIntensity: state => state.general.surfintensity,
            frequency: state => state.general.frequency
        }),

        ...mapState('exports', {
            displayExportForm: state => state.formExport.display
        }),

        ...mapState('account', {
            accountId: state => state.accountId
        }),

        ...mapGetters('builder', ['criteriaList', 'inEditionMode', 'audienceInEdition']),

        ...mapGetters('account', ['getAccessToBuilderSegment']),

        ...mapGetters('ui/builder', ['criteriaListIsReady']),

        dataTabs() {
            const dataTabs = [{ label: 'volumes' }, { label: 'breakdown' }]
            return this.demographicBreakdown ? dataTabs : filter(dataTabs, i => i.label !== 'breakdown')
        },

        displayAudienceForm() {
            return this.displayForm && this.audienceFormMode === 'audience'
        },

        throttling() {
            return cloneDeep(THROTTLING)
        },

        volumes() {
            return cloneDeep(VOLUMES)
        },

        maxExclusionBox() {
            return this.exclusionBox.length >= 1
        },

        hasUserAccessToBuilderSegment() {
            return this.getAccessToBuilderSegment()
        },

        editedAudienceIsInFormula() {
            if (this.inEditionMode) {
                return this.formulaInstance.includeCriterion(this.audienceInEdition.crossId)
            }
            return false
        }
    },

    methods: {
        ...mapMutations('builder', ['setFormula', 'resetFormula']),

        ...mapMutations('ui/builder', ['setActiveTabBuilder', 'setDisplayModal']),

        ...mapActions('ui/builder', ['createAudienceForm', 'closeAudienceForm', 'openEditAudienceForm']),

        ...mapActions('builder', ['getFormulaMetrics']),

        triggerEditionModal(status) {
            this.showEditionModal = status
        },

        closeForm() {
            this.closeAudienceForm()
        },

        onFormulaChange(formula) {
            this.setFormula(formula)
            this.loadMetrics(formula)
        },

        onFormulaMessage({ type, msg }) {
            this.$store.commit('notifications/setAddedNotification', {
                type,
                message: msg
            })
        },

        loadMetrics: debounce(function(formula) {
            this.getFormulaMetrics({ type: 'Volumes', formula })
            this.latestFormula = formula
        }, 500),

        triggerBreakdownMetrics: debounce(function() {
            this.getFormulaMetrics({ type: 'Breakdown', formula: this.latestFormula })
        }, 500),

        openAudienceForm() {
            this.formulaInstance.cleanBoxes()
            this.createAudienceForm()
        },

        manageSurfIntensity(value) {
            value ? this.formulaInstance.defaultSurfIntensity.show() : this.formulaInstance.defaultSurfIntensity.hide()
        },

        manageQuantile(value) {
            value ? this.formulaInstance.defaultQuantile.show() : this.formulaInstance.defaultQuantile.hide()
        },

        manageFrequency(value) {
            this.formulaInstance.setFrequencyOpts(value)
        },

        beforeEditModalAction(action) {
            this.$emit('closeBeforeEditModal')
            this.closeEditionModal()
            if (action === 'create') this.createAudienceForm()
            if (action === 'edit') {
                this.openEditAudienceForm()
                this.setDisplayModal({ item: 'warning', display: false })
            }
        },

        closeEditionModal() {
            this.showEditionModal = false
        }
    }
}
</script>

<style lang="scss" scoped>
.wam-content.builder-segment {
    grid-gap: 40px;
}
</style>
